import {ChangeEvent, ChangeEventHandler, ReactElement, useState} from "react";
import {DialCode, Address} from "@devour/client";
import {PatternFormat} from "react-number-format";
import Select from "@/components/inputs/Select";
import SelectOptionsFactory from "./SelectOptionsFactory";
import {NumberFormatValues} from "react-number-format/types/types";
import AddressAutocomplete from "./AddressAutocomplete";
import {Button} from "@/components/buttons/Button";
import useThemePreference from "@/hooks/useThemePreference";
import {useGetGeography} from "@/hooks/useGetGeography";

interface Props {
    placeholder?: string;
    value: Address;
    onChange: (newAddress: Address) => void;
    defaultMode?: "autocomplete" | "manual";
    showModeChangeButton?: boolean;
}

function AddressInputGroup({
    placeholder,
    value,
    onChange,
    defaultMode = "autocomplete",
    showModeChangeButton = true,
}: Props): ReactElement {

    const [
        autocompleteMode,
        setAutocompleteMode,
    ] = useState<"autocomplete" | "manual">(defaultMode);
    const { isOnDarkMode } = useThemePreference();
    const { data: geography = {countries: [], states: {}, provinces: {}} } = useGetGeography();

    /**
     * Handle changing either of the values and pass the combined entry to the callback.
     *
     * @param e
     */
    function countryOnChange(e: ChangeEvent<HTMLSelectElement>): void {
        onChange({
            ...value,
            country: e.target.value,
            // Reset place ID & coordinates if user manually changes an input
            placeId: undefined,
            location: undefined,
        });
    }

    /**
     * Handle all text input onChange events.
     *
     * @param key
     */
    function inputOnChange(key: keyof Address): ChangeEventHandler<HTMLInputElement | HTMLSelectElement> {
        return (e) => {
            onChange({
                ...value,
                [key]: e.target.value,
                // Reset place ID & coordinates if user manually changes an input
                placeId: undefined,
                location: undefined,
            });
        };
    }

    /**
     * Handle zip code onChange events.
     *
     * @param values
     */
    function zipCodeOnChange(values: NumberFormatValues): void {
        onChange({
            ...value,
            postalCode: values.value,
            // Reset place ID & coordinates if user manually changes an input
            placeId: undefined,
            location: undefined,
        });
    }

    function autocompleteCallback(newAddress: Address) {
        onChange(newAddress);
        setAutocompleteMode("manual");
    }

    if (autocompleteMode === "autocomplete") {
        return (
            <div className="dp-address-input">
                <AddressAutocomplete
                    callback={autocompleteCallback}
                    placeholder={placeholder}
                />
                {showModeChangeButton &&
                <Button variant={isOnDarkMode ? "primary" : "ghost"}
                    {...{type: "button"}}


                    className="dp-address-input_manual-button"
                    onClick={() => setAutocompleteMode("manual")}
                >
                    Manual Entry
                </Button>
                }
            </div>
        );
    }
    return (
        <div className="dp-address-input">
            {showModeChangeButton &&
                <div className="dp-address-input_row">
                    <Button variant={isOnDarkMode ? "primary" : "ghost"}
                        {...{type: "button"}}


                        className="dp-address-input_autocomplete-button"
                        onClick={() => setAutocompleteMode("autocomplete")}
                    >
                        Use Autocomplete
                    </Button>
                </div>
            }
            <div className="dp-address-input_row">
                <div className="dp-address-input_field dp-address-input_line1-container">
                    <label>Address Line 1</label>
                    <input
                        type="text"
                        placeholder="Address Line 1"
                        value={value.line1}
                        onChange={inputOnChange("line1")}
                        required={true}
                    />
                </div>
            </div>

            <div className="dp-address-input_row">
                <div className="dp-address-input_field dp-address-input_line2-container">
                    <label>Address Line 2</label>
                    <input
                        type="text"
                        placeholder="Address Line 2 (optional)"
                        value={value.line2}
                        onChange={inputOnChange("line2")}
                        required={false}
                    />
                </div>
            </div>

            <div className="dp-address-input_row">
                <div className="dp-address-input_field dp-address-input_city-container">
                    <label>City</label>
                    <input
                        type="text"
                        placeholder="City"
                        value={value.locality}
                        onChange={inputOnChange("locality")}
                        required={true}
                    />
                </div>

                <div className="dp-address-input_field dp-address-input_state-container">
                    {value.country === "US" &&
                        <>
                            <label>State</label>
                            <Select
                                value={value.administrativeArea}
                                onChange={inputOnChange("administrativeArea")}
                                required={true}
                            >
                                <option value="">Select State</option>
                                <SelectOptionsFactory
                                    options={Object.keys(geography.states).map((key) => {
                                        return {
                                            value: key,
                                            label: geography.states[key],
                                        };
                                    })}
                                />
                            </Select>
                        </>
                    }
                    {value.country === "CA" &&
                        <>
                            <label>Province</label>
                            <Select
                                value={value.administrativeArea}
                                onChange={inputOnChange("administrativeArea")}
                                required={true}
                            >
                                <option value="">Select Province</option>
                                <SelectOptionsFactory
                                    options={Object.entries(geography.provinces ?? {}).map(([key, name]) => {
                                        return {
                                            value: key,
                                            label: name,
                                        };
                                    })}
                                />
                            </Select>
                        </>
                    }
                    {value.country !== "US" && value.country !== "CA" &&
                        <>
                            <label>State / Province</label>
                            <input
                                type="text"
                                placeholder="State / Province"
                                value={value.administrativeArea}
                                onChange={inputOnChange("administrativeArea")}
                                required={false}
                            />
                        </>
                    }
                </div>
            </div>

            <div className="dp-address-input_row">
                <div className="dp-address-input_field dp-address-input_zip-code-container">
                    {value.country === "US" &&
                        <>
                            <label>Zip Code</label>
                            <PatternFormat
                                value={value.postalCode}
                                valueIsNumericString={true}
                                type="text"
                                format="#####"
                                placeholder="Zip Code"
                                className="dp-address-input_text"
                                onValueChange={zipCodeOnChange}
                            />
                        </>
                    }
                    {value.country === "CA" &&
                        <>
                            <label>Postal Code</label>
                            <input
                                type="text"
                                placeholder="Postal Code"
                                value={value.postalCode}
                                onChange={inputOnChange("postalCode")}
                                required={true}
                            />
                        </>
                    }
                    {value.country !== "US" && value.country !== "CA" &&
                        <>
                            <label>Zip Code / Postal Code</label>
                            <input
                                type="text"
                                placeholder="Zip Code / Postal Code"
                                value={value.postalCode}
                                onChange={inputOnChange("postalCode")}
                                required={false}
                            />
                        </>
                    }
                </div>

                <div className="dp-address-input_field dp-address-input_line2-container">
                    <label>Country</label>

                    <Select
                        value={value.country}
                        onChange={countryOnChange}
                    >
                        <SelectOptionsFactory
                            <DialCode>
                            options={geography.countries.map((d) => {
                                return {
                                    value: d.code,
                                    label: d.name,
                                };
                            })}
                        />
                    </Select>
                </div>
            </div>
        </div>
    );

}

export default AddressInputGroup;
